import { type DeliveryNote } from '~/types/deliveryNote';
import { type LineItem } from '~/types/deliveryNoteLineItem';

import { mergeDeep, withoutObjectKeysWhereValueIs } from '@vestigas/utils';

import { DELIVERY_NOTE_STRUCTURE } from '../constants/deliveryNote';
import {
  CONCRETE_COMPONENT_STRUCTURE,
  DELIVERY_NOTE_LINE_ITEM_STRUCTURE,
} from '../constants/lineItem';

/**
 * Merges a partial object into a default structure so that every path the
 * structure declares is present afterwards.
 *
 * `mergeDeep` lets source values win outright and recurses into a key only when
 * both sides hold a plain object, so an explicit `null` or `undefined` on the
 * partial replaces a nested default structure instead of being filled in by it.
 * Dropping those keys first turns them into the absent keys that `mergeDeep`
 * does fill from the defaults.
 *
 * Arrays are still replaced wholesale rather than merged element-wise, so
 * per-element defaults must be applied separately.
 */
const withStructure = <T>(structure: T, partial: object): T =>
  mergeDeep(
    structure as Record<string, unknown>,
    withoutObjectKeysWhereValueIs(partial as Record<string, unknown>, [
      null,
      undefined,
    ]),
  ) as T;

/**
 * Function to ensure the delivery note object has the correct structure.
 * Merges the provided delivery note object into the full default structure.
 * Values in the provided object override default values.
 *
 * @param {object} partialDLNObject - The partial delivery note object to be merged.
 * @return {Partial<DeliveryNote>} The structurally complete delivery note object.
 */
export const guaranteeDeliveryNoteStructure = (
  partialDLNObject: Partial<DeliveryNote>,
): DeliveryNote => withStructure(DELIVERY_NOTE_STRUCTURE, partialDLNObject);

/**
 * Function to ensure the line item object has the correct structure.
 * Merges the provided delivery note line item object into the full default structure.
 * Values in the provided object override default values.
 *
 * @param {object} partialLineItemObject - The partial delivery note line item object to be merged.
 * @return {Partial<DeliveryNote>} The structurally complete delivery note line item object.
 */
export const guaranteeDeliveryNoteLineItemStructure = (
  partialLineItemObject: Partial<LineItem>,
): LineItem => {
  const mergedLineItem = withStructure(
    DELIVERY_NOTE_LINE_ITEM_STRUCTURE,
    partialLineItemObject,
  );

  const finalLineItem = {
    ...mergedLineItem,
    product: {
      ...mergedLineItem.product,
      concrete: {
        ...mergedLineItem.product.concrete,
        // The merge above replaces the components array wholesale, so each
        // parsed component needs the component defaults applied to it.
        components: mergedLineItem.product.concrete.components.map(
          (component) => withStructure(CONCRETE_COMPONENT_STRUCTURE, component),
        ),
      },
    },
  };

  return finalLineItem;
};
